/**
 * What the screen actually says about sync, and why the wording is code rather than JSX.
 *
 * These strings are the product. A rep decides whether it is safe to leave a job site based
 * on this banner, so "queued" versus "synced" is a load-bearing distinction and not copy to
 * be tweaked inline. Pulling it into a plain module means the claims are unit-testable —
 * specifically the one claim that matters, that nothing says a photo is safe while this
 * phone is still the only place it exists.
 *
 * Two rules the tests enforce:
 *
 *  1. Never say "offline" as if it were a failure. Working with no signal is the normal
 *     case (ADR-0001 §3.1), and an alarming banner over an expected condition trains crews
 *     to ignore banners.
 *  2. Never imply durability the system has not achieved. There is no API yet (ADR-0002
 *     §2), so today's honest message is that work is queued on the device — not "saved".
 */

import type { PhotoSyncState } from '../db/repository.js';
import type { SyncSnapshot } from '../sync/engine.js';

/** Severity, for colour. Kept separate from the text so styling never picks the wording. */
export type Tone = 'good' | 'info' | 'warn' | 'bad';

export interface StatusLine {
  readonly tone: Tone;
  readonly text: string;
  /** Secondary line. Present when there is something useful to add, absent when not. */
  readonly detail?: string;
}

/**
 * The banner every screen carries.
 *
 * `unsynced` counts pending *and* failed ops (see `DexieOutboxStore.countUnsynced`), which
 * is why an "N waiting" message can coexist with an error: both are unfinished business.
 */
export function syncBanner(snapshot: SyncSnapshot): StatusLine {
  const unsynced = snapshot.counts.pending + snapshot.counts.failed;
  const waiting = `${String(unsynced)} ${unsynced === 1 ? 'item' : 'items'} waiting to upload`;

  switch (snapshot.state) {
    case 'unconfigured':
      // Deliberately not styled as an error. Nothing is wrong with the device or the
      // crew's work; the server this build would sync to has not been stood up yet.
      return {
        tone: 'info',
        text: unsynced === 0 ? 'Saved on this device' : `${waiting} — saved on this device`,
        detail: 'No upload server is configured for this build. Nothing is lost; nothing is off the phone yet.',
      };

    case 'offline':
      return {
        tone: 'warn',
        text: unsynced === 0 ? 'Offline — capture still works' : `Offline — ${waiting}`,
        detail: 'Photos are stored on this device and will upload when you have signal.',
      };

    case 'syncing':
      return { tone: 'info', text: unsynced === 0 ? 'Checking…' : `Uploading — ${waiting}` };

    case 'error':
      return {
        tone: 'bad',
        text: unsynced === 0 ? 'Last upload failed' : `Upload failed — ${waiting}`,
        detail: snapshot.lastError ?? 'Your work is queued on this device and will retry.',
      };

    case 'idle':
      return unsynced === 0
        ? { tone: 'good', text: 'All work uploaded' }
        : // Online, no error, and still holding items: the queue is mid-flight or backing
          // off. Saying "all uploaded" here would be the exact lie this module exists to
          // prevent.
          { tone: 'warn', text: waiting };
  }
}

/** The chip under a thumbnail. Short, because it sits on a phone in sunlight. */
export function photoStatus(state: PhotoSyncState): StatusLine {
  switch (state) {
    case 'queued':
      return { tone: 'warn', text: 'On device' };
    case 'recorded':
      // Metadata reached the server, bytes did not. The office knows the photo exists and
      // cannot open it, so this is not "done" and must not read as done.
      return { tone: 'info', text: 'Uploading' };
    case 'secured':
      return { tone: 'good', text: 'Uploaded' };
    case 'blocked':
      return { tone: 'bad', text: 'Needs attention' };
  }
}

/** Storage held on the device, in units a person can act on. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${String(Math.round(bytes / 1024))} KB`;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}

/**
 * The device's belief about when the shutter closed.
 *
 * Rendered in local time with no timezone gymnastics, because it is shown back to the
 * person who took the shot minutes ago. It is never used for ordering — ADR-0001 §4.5 —
 * and the server's `receivedAt` is the honest timestamp for anything official.
 */
export function formatCapturedAt(iso: string, locale?: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return 'unknown time';
  return at.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
}
